import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface PickerOption {
  id: string;
  label: string;
  /** Second, grey line (an e-mail, a serial number); searched too. */
  detail?: string;
}

type Props = {
  options: PickerOption[];
  selectedId: string;
  onSelect: (id: string) => void;
  searchLabel: string;
  emptyLabel: string;
  loading: boolean;
};

/** A searchable single-choice list. Plain Tailwind: the Polaris pickers open popovers a Headless UI dialog cannot reach. */
export default function OptionPicker({ options, selectedId, onSelect, searchLabel, emptyLabel, loading }: Props) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) => `${option.label} ${option.detail || ""}`.toLowerCase().includes(needle));
  }, [options, query]);

  return (
    <div className="space-y-2">
      <input
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        // Enter here searches; it must not submit the dialog around the picker.
        onKeyDown={(event) => event.key === "Enter" && event.preventDefault()}
        placeholder={searchLabel}
        aria-label={searchLabel}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-base md:text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
      <div className="max-h-64 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
        {loading ? (
          <div className="px-3 py-2 text-sm text-gray-500">{t("loading")}</div>
        ) : shown.length === 0 ? (
          <div className="px-3 py-2 text-sm text-gray-500">{emptyLabel}</div>
        ) : (
          shown.map((option) => (
            <label key={option.id} className="flex cursor-pointer items-start gap-2 px-3 py-2 text-sm hover:bg-gray-50 max-md:min-h-11 max-md:py-3">
              <input
                type="radio"
                name="option-picker"
                checked={selectedId === option.id}
                onChange={() => onSelect(option.id)}
                className="mt-0.5 h-4 w-4 shrink-0 border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="min-w-0">
                <span className="block text-gray-900">{option.label}</span>
                {option.detail && <span className="block truncate text-xs text-gray-500">{option.detail}</span>}
              </span>
            </label>
          ))
        )}
      </div>
    </div>
  );
}
